import { ActionTree } from 'vuex';
import {
  ETemplateLibraryAction,
  ETemplateLibraryMutation,
  state as defaultState,
} from '@/dealroom/create-page/template-library-old/store/template-library-old.constants';
import { ITemplateLibraryState } from '@/dealroom/create-page/template-library-old/store/template-library-old.types';
import { IDocument } from '@/types/Document';
import { TemplateService } from '@/api/templates/template.service';
import { RootState } from '@/store/store';
import { FolderService } from '@/api/folders/services/folder.service';
import { translate } from '@/helpers/translation.helper';
import { EFolderType } from '@/folders/types/enums/folder-type';
import { IFolder } from '@/folders/types/folder';
import { IFolderTree } from '@/folders/types/folder-tree';
import { IPagination } from '@/types/Pagination';

export const actions: ActionTree<ITemplateLibraryState, RootState> = {
  async [ETemplateLibraryAction.LoadTemplates]({ dispatch, commit, state }) {
    commit(ETemplateLibraryMutation.SetLoading, true);
    commit(ETemplateLibraryMutation.SetLoaded, false);
    const { pageSize, currentPage }: IPagination = state.pagination;
    try {
      const response: {
        templates: IDocument[];
        totalCount: number;
      } = await TemplateService.loadTemplates(
        state.folderId,
        pageSize * (currentPage - 1), // The pagination component handles 1 as first page, this will change
        pageSize - 1, // Apparently the endpoint is starting on 0
        state.searchQuery
      );
      dispatch(ETemplateLibraryAction.LoadTemplatesSuccess, response);
    } catch (error) {
      dispatch(ETemplateLibraryAction.LoadTemplatesFailed, error);
    }
  },
  [ETemplateLibraryAction.LoadTemplatesSuccess](
    { commit },
    { templates, totalCount }: { templates: IDocument[]; totalCount: number }
  ) {
    commit(ETemplateLibraryMutation.SetTemplates, { templates, totalRows: totalCount });
    commit(ETemplateLibraryMutation.SetLoaded, true);
    commit(ETemplateLibraryMutation.SetLoading, false);
  },
  [ETemplateLibraryAction.LoadTemplatesFailed]({ commit, dispatch }) {
    commit(ETemplateLibraryMutation.SetTemplates, { templates: [], totalRows: 0 });
    commit(ETemplateLibraryMutation.SetLoaded, false);
    commit(ETemplateLibraryMutation.SetLoading, false);
    dispatch('alert/showDangerAlert', translate('failed-to-load-templates'), {
      root: true,
    });
  },
  async [ETemplateLibraryAction.LoadFolders]({ dispatch, state }) {
    try {
      const folders: IFolder[] = await FolderService.loadFolders(
        state.folderId,
        EFolderType.Template
      );
      dispatch(ETemplateLibraryAction.LoadFoldersSuccess, folders);
    } catch (err) {
      dispatch(ETemplateLibraryAction.LoadFoldersFailed);
    }
  },
  [ETemplateLibraryAction.LoadFoldersSuccess]({ commit }, folders: IFolder[]) {
    commit(ETemplateLibraryMutation.SetFolders, folders);
  },
  [ETemplateLibraryAction.LoadFoldersFailed]({ dispatch }) {
    dispatch('alert/showDangerAlert', translate('failed-to-load-templates'), {
      root: true,
    });
  },
  async [ETemplateLibraryAction.LoadBreadcrumbs]({ dispatch, state }) {
    try {
      const breadcrumbs: IFolder[] = await FolderService.loadBreadcrumbs(
        state.folderId,
        EFolderType.Template
      );
      dispatch(ETemplateLibraryAction.LoadBreadcrumbsSuccess, breadcrumbs);
    } catch (err) {
      dispatch(ETemplateLibraryAction.LoadTemplatesFailed);
    }
  },
  [ETemplateLibraryAction.LoadBreadcrumbsSuccess]({ commit }, breadcrumbs: IFolder[]) {
    const rootFolder: IFolder = { id: null, name: 'Start' };
    commit(ETemplateLibraryMutation.SetBreadcrumbs, [rootFolder, ...breadcrumbs]);
  },
  async [ETemplateLibraryAction.LoadFolderTree]({ dispatch }) {
    try {
      const folderTree: IFolderTree = await FolderService.loadFolderTree(EFolderType.Template);
      dispatch(ETemplateLibraryAction.LoadFolderTreeSuccess, folderTree);
    } catch (err) {
      dispatch(ETemplateLibraryAction.LoadTemplatesFailed);
    }
  },
  [ETemplateLibraryAction.LoadFolderTreeSuccess]({ commit }, folderTree: IFolderTree) {
    commit(ETemplateLibraryMutation.SetFolderTree, folderTree);
  },
  [ETemplateLibraryAction.SetFolderId]({ commit, dispatch }, folderId: string) {
    commit(ETemplateLibraryMutation.SetFolderId, folderId);
    dispatch(ETemplateLibraryAction.LoadFolders);
    dispatch(ETemplateLibraryAction.LoadBreadcrumbs);
    dispatch(ETemplateLibraryAction.LoadTemplates);
  },
  [ETemplateLibraryAction.SetPagination]({ commit, dispatch, state }, data: Partial<IPagination>) {
    commit(ETemplateLibraryMutation.SetPagination, { ...state.pagination, ...data });
    dispatch(ETemplateLibraryAction.LoadTemplates);
  },
  [ETemplateLibraryAction.Search]({ commit, dispatch }, query: string) {
    commit(ETemplateLibraryAction.SetPagination, defaultState.pagination);
    commit(ETemplateLibraryMutation.SetSearchQuery, query);
    dispatch(ETemplateLibraryAction.LoadTemplates);
  },
  [ETemplateLibraryAction.Reset]({ commit }) {
    commit(ETemplateLibraryMutation.SetState, defaultState);
  },
};
